import axios, { InternalAxiosRequestConfig } from 'axios';
import { AuthResponse, ProfileResponse, ApiError, LinkedInConfigResponse, LinkedInSignInResponse, GoogleConfigResponse, GoogleSignInResponse, InterviewerItem, InterviewerRegistrationData, InterviewerRegistrationResponse, InterviewRequestItem, InterviewConfigResponse, InterviewRequestsResponse, InterviewReportsResponse, CurrentLegalDocumentsResponse, LegalDocument } from '../types';
import { API_CONFIG, API_PATHS } from '../config/api';
import { clearLocalToken } from '../auth/ssoHandshake';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
  }
}

function isPublicNavGet(config: InternalAxiosRequestConfig) {
  const method = (config.method || 'get').toLowerCase();
  const path = (config.url || '').split('?')[0];
  return method === 'get' && (path === '/v1/nav' || path.endsWith('/v1/nav'));
}

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-App-Brand': API_CONFIG.APP_BRAND,
  },
  timeout: 30000,
  withCredentials: true,
});

// Add a request interceptor to include token in headers
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!config.skipAuth && !isPublicNavGet(config)) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.error('Request setup error:', error);
    return Promise.reject(new Error('Failed to setup request. Please check your connection.'));
  }
);

// Enhanced response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    // Check if response indicates failure
    if (response.data && response.data.success === false) {
      let errorMessages: string[] = [];

      // Handle array of error objects
      if (response.data.errors && Array.isArray(response.data.errors)) {
        errorMessages = response.data.errors.map((error: any) => 
          error.message || error.toString()
        );
      }
      // Handle message field if present
      else if (response.data.message) {
        errorMessages.push(response.data.message);
      }

      if (errorMessages.length > 0) {
        const error = new Error(errorMessages.join('\n'));
        (error as any).isApiError = true;
        (error as any).errors = response.data.errors;
        throw error;
      }
    }
    return response;
  },
  (error) => {
    let errorMessage = 'An unexpected error occurred. Please try again.';

    if (error.response) {
      console.error('API Error Response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
      });

      // Handle API error response
      if (error.response.data) {
        const errorData = error.response.data;
        let errorMessages: string[] = [];

        // Handle array of error objects
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessages = errorData.errors.map((error: any) => 
            error.message || error.toString()
          );
        }
        // Handle message field if present
        else if (errorData.message) {
          errorMessages.push(errorData.message);
        }

        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join('\n');
        }
      } else {
        // Fallback error messages based on status code
        switch (error.response.status) {
          case 401:
            errorMessage = 'Unauthorized. Please log in again.';
            if (!(error.config as InternalAxiosRequestConfig | undefined)?.skipAuth && !isPublicNavGet(error.config || {})) {
              clearLocalToken();
              void api.post('/Auth/logout').catch(() => undefined);
            }
            break;
          case 403:
            errorMessage = 'Access denied. You do not have permission to perform this action.';
            break;
          case 404:
            // Special handling for profile not found
            if (error.config.url.includes('/UserProfile')) {
              error.message = 'User profile not found';
              return Promise.reject(error);
            }
            errorMessage = 'Resource not found. Please try again later.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = `Server error (${error.response.status}). Please try again later.`;
        }
      }
    } else if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
      console.error('API Timeout:', error.message);
      errorMessage = 'The request took too long. Please try again.';
    } else if (error.request) {
      console.error('API No Response:', error.request);
      errorMessage = 'Unable to reach the server. Please check your internet connection and try again.';
    } else {
      console.error('API Request Error:', error.message);
      errorMessage = 'Failed to make request. Please try again.';
    }

    error.message = errorMessage;
    return Promise.reject(error);
  }
);

export const authApi = {
  register: (email: string, password: string, emailConsentAccepted = true) =>
    api.post<AuthResponse>('/Auth/register', { email, password, emailConsentAccepted }),
  
  login: (email: string, password: string) => 
    api.post<AuthResponse>('/Auth/login', { email, password }),

  verifyLoginMfa: (mfaToken: string, code: number) =>
    api.post<AuthResponse>('/Auth/login/mfa', { mfaToken, code }),

  resendLoginMfa: (mfaToken: string) =>
    api.post<AuthResponse>('/Auth/login/mfa/resend', { mfaToken }),

  session: () => api.get<AuthResponse>('/Auth/session'),

  logout: () => api.post<AuthResponse>('/Auth/logout'),
  
  generateEmailVerificationCode: (email: string) => 
    api.post<AuthResponse>(`/Auth/user/email/generateverificationcode/${encodeURIComponent(email)}`),
  
  verifyEmail: (code: number) => 
    api.post('/Auth/user/email/verify', { code }),

  forgotPassword: (email: string, emailVerificationCode: number, newPassword: string) =>
    api.post<AuthResponse>('/Auth/forgotpassword', {
      email,
      emailVerificationCode,
      password: newPassword
    }),
};

export const userApi = {
  createProfile: (
    username: string,
    mobile: string,
    linkedInUrl?: string,
    userInfoJson?: string,
    options?: { emailConsentAccepted?: boolean; termsAccepted?: boolean; privacyAccepted?: boolean }
  ) =>
    api.post<ProfileResponse>('/UserProfile', {
      username,
      mobile,
      linkedInUrl: linkedInUrl || undefined,
      userInfoJson: userInfoJson || '{}',
      identityProviderId: 1,
      emailConsentAccepted: options?.emailConsentAccepted ?? true,
      termsAccepted: !!options?.termsAccepted,
      privacyAccepted: !!options?.privacyAccepted,
    }),
  
  getProfile: () => api.get<ProfileResponse>('/UserProfile'),

  acceptLegalDocuments: (payload: {
    termsAccepted: boolean;
    privacyAccepted: boolean;
    termsVersion?: string;
    privacyVersion?: string;
  }) => api.post<ProfileResponse>('/UserProfile/accept-legal', payload),

  getCurrentLegalDocuments: () =>
    api.get<CurrentLegalDocumentsResponse>('/LegalDocuments/current'),

  listLegalDocuments: () =>
    api.get<{ terms: LegalDocument[]; privacy: LegalDocument[]; success: boolean }>('/LegalDocuments'),

  publishLegalDocument: (payload: { documentType: 'Terms' | 'Privacy'; version: string; title: string; bodyHtml: string }) =>
    api.post<CurrentLegalDocumentsResponse>('/LegalDocuments', payload),

  createExternalProfile: (
    email: string,
    username: string,
    mobile: string,
    identityProviderId: number,
    linkedInUrl?: string,
    userInfoJson?: string,
    profileImageUrl?: string,
    options?: { emailConsentAccepted?: boolean; termsAccepted?: boolean; privacyAccepted?: boolean }
  ) =>
    api.post<ProfileResponse>('/UserProfile/CreateExternalProfile', {
      email,
      username,
      mobile,
      linkedInUrl: linkedInUrl || undefined,
      userInfoJson: userInfoJson || '{}',
      identityProviderId,
      profileImageUrl: profileImageUrl || undefined,
      emailConsentAccepted: options?.emailConsentAccepted ?? true,
      termsAccepted: !!options?.termsAccepted,
      privacyAccepted: !!options?.privacyAccepted,
    }),

  uploadProfilePhoto: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ success: boolean; message?: string; profileImageUrl?: string; displayImageUrl?: string }>(
      '/ProfilePhoto/photo',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  getLinkedInConfig: () =>
    api.get<LinkedInConfigResponse>('/UserProfile/linkedin/config'),

  linkedInSignIn: (code: string, redirectUri: string) =>
    api.post<LinkedInSignInResponse>('/UserProfile/linkedin/signin', {
      code,
      redirectUri,
    }),

  confirmLinkedInLink: (linkTicket: string) =>
    api.post<LinkedInSignInResponse>('/UserProfile/linkedin/confirm-link', {
      linkTicket,
    }),

  revokeLinkedIn: (accessToken: string) =>
    api.post('/UserProfile/linkedin/revoke', { accessToken }),

  getGoogleConfig: () =>
    api.get<GoogleConfigResponse>('/UserProfile/google/config'),

  googleSignIn: (code: string, redirectUri: string) =>
    api.post<GoogleSignInResponse>('/UserProfile/google/signin', {
      code,
      redirectUri,
    }),

  confirmGoogleLink: (linkTicket: string) =>
    api.post<GoogleSignInResponse>('/UserProfile/google/confirm-link', {
      linkTicket,
    }),

  revokeGoogle: (accessToken: string) =>
    api.post('/UserProfile/google/revoke', { accessToken }),

  updateProfile: (payload: {
    username: string;
    userInfoJson?: string;
    professionalTitle?: string;
    biography?: string;
    linkedInUrl?: string;
    profileImageUrl?: string;
  }) => api.put<ProfileResponse>('/UserProfile', payload),

  adminListUsers: () =>
    api.get<{ users: AdminUser[]; roles: string[]; success: boolean }>('/UserProfile/admin/users'),
  adminCreateUser: (payload: {
    email: string;
    username?: string;
    mobile?: string;
    password: string;
    role: string;
    roles?: string[];
    isEmailVerified?: boolean;
    isActive?: boolean;
    courseOwnerRevenuePercent?: number | null;
  }) => api.post<{ user?: AdminUser; success: boolean; message?: string }>('/UserProfile/admin/users', payload),
  adminUpdateUser: (id: number, payload: Record<string, unknown>) =>
    api.put<{ user?: AdminUser; success: boolean; message?: string }>(`/UserProfile/admin/users/${id}`, payload),
  adminSetRole: (id: number, role: string) =>
    api.put<{ user?: AdminUser; success: boolean; message?: string }>(`/UserProfile/admin/users/${id}/role`, { role }),
  adminSetRoles: (id: number, roles: string[]) =>
    api.put<{ user?: AdminUser; success: boolean; message?: string }>(`/UserProfile/admin/users/${id}/role`, { roles }),
  adminDeactivateUser: (id: number) =>
    api.delete<{ user?: AdminUser; success: boolean; message?: string }>(`/UserProfile/admin/users/${id}`),
};

export interface AdminUser {
  id: number;
  email: string;
  username: string;
  mobile: string;
  role?: string;
  roles?: string[];
  roleId?: number;
  isActive: boolean;
  isEmailVerified: boolean;
  isMobileVerified?: boolean;
  identityProviderId?: number;
  identityProvider?: string;
  courseOwnerRevenuePercent?: number | null;
  userInfoJson?: string | null;
  linkedInUrl?: string;
}

export const academyAdminApi = {
  enrollmentsByCourse: (courseId: number) =>
    api.get<{
      courseEnrollments: {
        id: number;
        courseId: number;
        userId: number;
        isActive?: boolean;
        status: string;
        enrollmentDate: string;
        discountPercent?: number;
        courseTitle?: string;
      }[];
      success?: boolean;
    }>(`/Enrollments/course/${courseId}`),
  enrollmentsByUsers: (userProfileIds: number[]) =>
    api.post<{
      users: { userProfileId: number; enrolledCourses: { courseId: number; courseTitle?: string; status: string; enrollmentDate: string; discountPercent?: number }[] }[];
      success: boolean;
    }>('/Enrollments/admin/by-users', { userProfileIds }),
  enrollmentsByUser: (userProfileId: number) =>
    api.get<{
      userProfileId: number;
      enrolledCourses: { id: number; courseId: number; courseTitle?: string; status: string; enrollmentDate: string; discountPercent?: number; isActive?: boolean }[];
    }>(`/Enrollments/user/${userProfileId}`),
};

export interface InquiryResponse {
  contact: {
    id: number;
    name: string;
    email: string;
    subject: string;
    message: string;
    contactType: string;
    createdDate: string;
  };
  id: string;
  success: boolean;
  message: string | null;
  errors: ApiError[] | null;
}

export const inquiryApi = {
  create: (name: string, email: string, subject: string, message: string) => 
    api.post<InquiryResponse>('/Inquiries/create', {
      inquiry: {
        name,
        email,
        subject,
        message,
        contactType: 'GeneralInquiry',
        createdDate: new Date().toISOString()
      }
    }),
};

export const mentorApi = {
  register: (mentor: Record<string, unknown>) =>
    api.post('/Mentor/register', { mentor }),
  list: (status?: string) =>
    api.get<{ mentors: Array<Record<string, unknown>>; success: boolean; message?: string }>(
      status ? `/Mentor?status=${encodeURIComponent(status)}` : '/Mentor'
    ),
  myApplication: () =>
    api.get<{
      hasApplication: boolean;
      role?: string;
      displayStatus?: string | null;
      approvalStatus?: string | null;
      rejectionReason?: string | null;
      success: boolean;
      message?: string;
    }>('/Mentor/my-application'),
  get: (mentorId: number) =>
    api.get<{ mentor?: Record<string, unknown>; success: boolean; message?: string }>(`/Mentor/${mentorId}`),
  update: (mentorId: number, data: Record<string, unknown>) =>
    api.put<{ mentor?: Record<string, unknown>; success: boolean; message?: string }>(`/Mentor/${mentorId}`, data),
  approve: (mentorId: number) => api.put(`/Mentor/${mentorId}/approve`),
  reject: (mentorId: number, reason?: string) => api.put(`/Mentor/${mentorId}/reject`, { reason }),
  programMentors: (courseId: number) =>
    api.get<{ mentors: Array<Record<string, unknown>>; success: boolean }>(`/programs/${courseId}/mentors`),
  assignProgramMentor: (courseId: number, mentorId: number) =>
    api.post(`/programs/${courseId}/mentors`, { mentorId }),
  removeProgramMentor: (courseId: number, mentorId: number) =>
    api.delete(`/programs/${courseId}/mentors/${mentorId}`),
  academyPrograms: () =>
    api.get<{ courses?: Array<{ id: number; title?: string; display_Title?: string }> }>('/programs?pageSize=50'),
  
  getMentorProfile: (mentorId: string) =>
    api.get(`/Mentor/${mentorId}`),

  verifyEmail: (email: string, code: number) =>
    api.post(API_PATHS.mentor.verifyEmail, { email, code }),

  resendVerification: (email: string) =>
    api.post(API_PATHS.mentor.resendVerification(email)),
};

export interface InterviewConfig {
  defaultDurationMinutes: number;
  defaultPrice: number;
  discountPercent: number;
  payablePrice: number;
  technologies: string[];
  maxActiveSlots?: number;
  activeSlotCount?: number;
  seatsFull?: boolean;
}

export interface AddInterviewPayload {
  userProfileId: number;
  technology: string;
  otherTechnology?: string;
  yearsOfExperience: number;
  experienceDetails: string;
  role: string;
  roleDetails: string;
  linkedInUrl: string;
  additionalNotes?: string;
  contentCourseId?: number;
  contentCourseTitle?: string;
  courseInterviewSession?: number;
}

export const interviewerApi = {
  register: (data: InterviewerRegistrationData) =>
    api.post<InterviewerRegistrationResponse>(API_PATHS.interviewer.register, { interviewer: data }),

  verifyEmail: (email: string, code: number) =>
    api.post(API_PATHS.interviewer.verifyEmail, { email, code }),

  resendVerification: (email: string) =>
    api.post(API_PATHS.interviewer.resendVerification(email)),

  applications: (status?: string) =>
    api.get<{ interviewers: InterviewerItem[]; success: boolean; message?: string }>(
      status ? `/Interviewer/applications?status=${encodeURIComponent(status)}` : '/Interviewer/applications'
    ),
  myApplication: () =>
    api.get<{
      hasApplication: boolean;
      role?: string;
      displayStatus?: string | null;
      approvalStatus?: string | null;
      rejectionReason?: string | null;
      success: boolean;
      message?: string;
    }>('/Interviewer/my-application'),
  get: (interviewerId: number) =>
    api.get<{ interviewer?: InterviewerItem; success: boolean; message?: string }>(`/Interviewer/${interviewerId}`),
  update: (interviewerId: number, data: Partial<InterviewerItem>) =>
    api.put<{ interviewer?: InterviewerItem; success: boolean; message?: string }>(`/Interviewer/${interviewerId}`, data),
  approve: (interviewerId: number) => api.put(`/Interviewer/${interviewerId}/approve`),
  reject: (interviewerId: number, reason?: string) => api.put(`/Interviewer/${interviewerId}/reject`, { reason }),
  list: (technology?: string) =>
    api.get<{ interviewers: InterviewerItem[]; success: boolean; message?: string }>(
      API_PATHS.interviewer.list,
      { params: technology ? { technology } : undefined }
    ),
};

export const interviewApi = {
  getConfig: () => api.get<InterviewConfigResponse>(API_PATHS.interviews.config),
  request: (data: AddInterviewPayload) =>
    api.post<InterviewRequestsResponse & { interviewRequest?: InterviewRequestItem; success: boolean; message?: string }>(
      API_PATHS.interviews.request,
      data
    ),
  getByUser: (userProfileId: number) =>
    api.get<InterviewRequestsResponse>(API_PATHS.interviews.byUser(userProfileId)),
  getAssigned: () => api.get<InterviewRequestsResponse>(API_PATHS.interviews.assigned),
  getReports: () => api.get<InterviewReportsResponse>(API_PATHS.interviews.reports),
  getById: (interviewRequestId: number) =>
    api.get<{ interviewRequest?: InterviewRequestItem; success: boolean; message?: string }>(API_PATHS.interviews.byId(interviewRequestId)),
  update: (interviewRequestId: number, data: Omit<AddInterviewPayload, 'userProfileId'>) =>
    api.put<{ interviewRequest?: InterviewRequestItem; success: boolean; message?: string }>(API_PATHS.interviews.byId(interviewRequestId), data),
  getAll: () => api.get<InterviewRequestsResponse>(API_PATHS.interviews.list),
  adminCreate: (data: {
    userProfileId: number;
    interviewerId: number;
    technology: string;
    otherTechnology?: string;
    scheduledDate: string;
    scheduledTime: string;
    meetingLink: string;
    yearsOfExperience?: number;
    role?: string;
    roleDetails?: string;
    experienceDetails?: string;
    linkedInUrl?: string;
    additionalNotes?: string;
    price?: number;
  }) => api.post<InterviewRequestsResponse & { interviewRequest?: InterviewRequestItem; success: boolean; message?: string }>(
    API_PATHS.interviews.adminCreate,
    data
  ),
  schedule: (interviewRequestId: number, data: { meetingLink: string; scheduledDate: string; scheduledTime: string; userEmail?: string }) =>
    api.put(API_PATHS.interviews.schedule(interviewRequestId), data),
  assign: (interviewRequestId: number, interviewerId: number, userEmail?: string) =>
    api.put(API_PATHS.interviews.assign(interviewRequestId), { interviewerId, userEmail }),
  reject: (interviewRequestId: number, reason?: string, userEmail?: string) =>
    api.put(API_PATHS.interviews.reject(interviewRequestId), { reason, userEmail }),
  acceptInterest: (interviewRequestId: number) =>
    api.put<{ interviewRequest?: InterviewRequestItem; success: boolean; message?: string }>(
      API_PATHS.interviews.acceptInterest(interviewRequestId)
    ),
  feedback: (interviewRequestId: number, data: { feedback: string; markCompleted?: boolean }) =>
    api.put<{ interviewRequest?: InterviewRequestItem; success: boolean; message?: string }>(API_PATHS.interviews.feedback(interviewRequestId), data),
  delete: (interviewRequestId: number, userEmail?: string) =>
    api.delete(API_PATHS.interviews.delete(interviewRequestId), { params: userEmail ? { userEmail } : undefined }),
};

export default api;